import { recipeFor, renderSvg, stateLabel, type RenderOptions } from "./render.ts";
import { STATES, type Recipe, type VtObject } from "./types.ts";
import { getTheme, themeToCss, themeToDesignTokens } from "./themes.ts";

/**
 * Exporters are thin adapters over the canonical Visual Object. Each one only
 * adds the packaging or conventions its destination needs.
 */

export interface ExportFile {
  filename: string;
  mime: string;
  content: string;
}

export const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "symbol";

export function baseName(vo: VtObject, opts: Pick<RenderOptions, "state" | "style">): string {
  return slugify([vo.label?.text, vo.identity.kind, opts.style, opts.state].filter(Boolean).join("-"));
}

/** Level A — universal SVG with literal colours for the chosen state. */
export function exportSvg(vo: VtObject, opts: RenderOptions): ExportFile {
  return {
    filename: `${baseName(vo, opts)}.svg`,
    mime: "image/svg+xml",
    content: `<?xml version="1.0" encoding="UTF-8"?>\n${renderSvg(vo, { ...opts, mode: "resolved" })}\n`,
  };
}

/** Smart SVG — every state embedded, switched with data-vt-state; themable via CSS variables. */
export function exportSmartSvg(vo: VtObject, opts: RenderOptions): ExportFile {
  const name = slugify([vo.label?.text, vo.identity.kind, opts.style, "smart"].filter(Boolean).join("-"));
  return {
    filename: `${name}.svg`,
    mime: "image/svg+xml",
    content: `<?xml version="1.0" encoding="UTF-8"?>\n${renderSvg(vo, { ...opts, mode: "themable" })}\n`,
  };
}

/** One resolved SVG per state — convenient for Ignition image swaps by state. */
export function exportStateSet(vo: VtObject, opts: RenderOptions): ExportFile[] {
  return STATES.filter((s) => vo.states.includes(s)).map((state) => exportSvg(vo, { ...opts, state }));
}

/** The canonical Visual Object plus the reproducing recipe. */
export function exportObject(vo: VtObject, opts: RenderOptions): ExportFile {
  const doc = { ...vo, recipe: recipeFor(vo, opts) };
  return {
    filename: `${slugify([vo.label?.text, vo.identity.kind].filter(Boolean).join("-"))}.vt.json`,
    mime: "application/json",
    content: JSON.stringify(doc, null, 2) + "\n",
  };
}

export function exportRecipe(recipe: Recipe, name: string): ExportFile {
  return { filename: `${slugify(name)}.recipe.json`, mime: "application/json", content: JSON.stringify(recipe, null, 2) + "\n" };
}

/**
 * Level D — Ignition starter metadata. Guidance only: suggested state names
 * and region element ids so a Perspective developer can wire bindings in
 * Ignition. It is not a binding engine and makes no control/safety claims.
 */
export function exportIgnitionMetadata(vo: VtObject, opts: RenderOptions): ExportFile {
  const prefix = opts.idPrefix ?? vo.id;
  const meta = {
    $comment:
      "Visual Toolkit starter metadata for Ignition. Suggested conventions only — Ignition owns tags, bindings, alarming and events.",
    object: vo.identity,
    generator: vo.generator,
    style: opts.style,
    theme: opts.theme,
    states: vo.states.map((s) => ({
      value: s,
      label: stateLabel(s),
      file: `${baseName(vo, { ...opts, state: s })}.svg`,
    })),
    regions: vo.regions.map((r) => ({ id: r.id, elementId: `${prefix}-${r.id}`, role: r.role, label: r.label })),
    ports: vo.ports,
    suggestedExpression:
      "Map your equipment status tag to one of the state values above, then select the matching SVG (image source) or data-vt-state (smart SVG).",
  };
  return {
    filename: `${slugify([vo.label?.text, vo.identity.kind].filter(Boolean).join("-"))}.ignition.json`,
    mime: "application/json",
    content: JSON.stringify(meta, null, 2) + "\n",
  };
}

export function exportThemeCss(themeId: RenderOptions["theme"]): ExportFile {
  const t = getTheme(themeId);
  return { filename: `${t.id}.css`, mime: "text/css", content: themeToCss(t) };
}

export function exportThemeTokens(themeId: RenderOptions["theme"]): ExportFile {
  const t = getTheme(themeId);
  return { filename: `${t.id}.tokens.json`, mime: "application/json", content: JSON.stringify(themeToDesignTokens(t), null, 2) + "\n" };
}

export { zip, type ZipEntry } from "./zip.ts";

/**
 * Ignition kit: one resolved SVG per state (for image-source swaps), the
 * smart SVG, starter metadata, the canonical object and a short guide.
 */
export function exportIgnitionKit(vo: VtObject, opts: RenderOptions): { filename: string; files: ExportFile[] } {
  const states = exportStateSet(vo, opts);
  const meta = exportIgnitionMetadata(vo, opts);
  const guide: ExportFile = {
    filename: "README.txt",
    mime: "text/plain",
    content: [
      `${vo.label?.text ? vo.label.text + " — " : ""}${vo.identity.name} (${opts.style}, ${opts.theme})`,
      "Generated by Visual Toolkit (Grindstone Systems). Assets are yours to use; no runtime dependency.",
      "",
      "Perspective, simplest path:",
      "  1. Upload the SVGs in states/ to your project (e.g. drag into an Image component,",
      "     or host them via your preferred web resources approach).",
      "  2. Bind the Image source to an expression that maps your status tag to a state",
      `     name listed in ${meta.filename}, selecting the matching file.`,
      "",
      "smart.svg contains every state. It is intended for inline/web use where the",
      "data-vt-state attribute can be changed; behaviour inside Perspective components",
      "has not yet been validated — see docs/IGNITION.md in the Visual Toolkit repository.",
      "",
      "Ignition owns tags, bindings, alarming and events. These files are visual assets only",
      "and make no claim about control-system or safety correctness.",
      "",
    ].join("\n"),
  };
  const base = slugify([vo.label?.text, vo.identity.kind, opts.style].filter(Boolean).join("-"));
  return {
    filename: `${base}-ignition-kit.zip`,
    files: [
      ...states.map((f) => ({ ...f, filename: `states/${f.filename}` })),
      { ...exportSmartSvg(vo, opts), filename: "smart.svg" },
      meta,
      exportObject(vo, opts),
      guide,
    ],
  };
}
